/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import * as constants from "./utils/constants";

const B2C_TENANT = process.env.REACT_APP_B2C_TENANT || "dommicrosso";
const B2C_TENANT_FULL = `${B2C_TENANT}.onmicrosoft.com`;
const B2C_AUTHORITY_DOMAIN = `${B2C_TENANT}.b2clogin.com`;
const B2C_AUTHORITY_BASE = `https://${B2C_AUTHORITY_DOMAIN}/${B2C_TENANT_FULL}`;
const AZURE_CLIENT_ID =
  process.env.REACT_APP_AZURE_CLIENT_ID || "1defca2b-2a56-44ab-8e58-588fa9560b49";
const API_RESOURCE_URI = `https://${B2C_TENANT_FULL}/dommicro`;

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: "B2C_1A_SIGNUP_SIGNIN",
    forgotPassword: "B2C_1A_PASSWORDRESET",
    editProfile: "B2C_1A_PROFILEEDIT",
  },
  authorities: {
    signUpSignIn: {
      authority: `${B2C_AUTHORITY_BASE}/B2C_1A_SIGNUP_SIGNIN`,
    },
    forgotPassword: {
      authority: `${B2C_AUTHORITY_BASE}/B2C_1A_PASSWORDRESET`,
    },
    editProfile: {
      authority: `${B2C_AUTHORITY_BASE}/B2C_1A_PROFILEEDIT`,
    },
  },
  authorityDomain: B2C_AUTHORITY_DOMAIN,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: "/", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: true,
    secureCookies: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  scopes: {
    read: [`${API_RESOURCE_URI}/read`],
    write: [`${API_RESOURCE_URI}/write`],
  },
  apiSelf: {
    endpoint: constants.apiBaseUrl + "SecurityV1/Self",
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    ...protectedResources.scopes.read,
    ...protectedResources.scopes.write,
  ],
};
